import { QuotaNotice } from "@/components/billing/quota-notice";
import { AccessBadge } from "@/components/app/access-badge";
import { useMemo, useState } from "react";
import { seo, breadcrumbScript } from "@/lib/seo";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { usePremium } from "@/components/billing/premium-lock";
import { useQuotaUpgrade } from "@/components/billing/use-quota-upgrade";
import { AnimatePresence, motion } from "motion/react";
import { BarChart3, ChevronRight, Crosshair, Plus, Sparkles } from "lucide-react";
import { createTest, fetchTests } from "@/lib/tests.functions";
import { formatPrettyDate, localDateString } from "@/lib/app-types";
import {
  STATUS_LABEL,
  STATUS_STYLE,
  daysBetween,
  percentOf,
  planProgress,
  testStatus,
  type TestChapterInput,
  type TestPlan,
} from "@/lib/test-plan";
import { AddTestSheet } from "@/components/app/add-test-sheet";
import { NeuralButton } from "@/components/app/neural-button";
import { friendlyError, notifySaved, trackSave } from "@/lib/save-feedback";
import { cn } from "@/lib/utils";
import { ShareButton } from "@/components/app/share-button";

export const Route = createFileRoute("/tests/")({
  head: () => ({
    ...seo({
      title: "Test Tracker — The Quantum Minds",
      description:
        "Plan every JEE & NEET test: pick chapters and auto-generate a day-by-day revision and question practice countdown.",
      path: "/tests",
    }),
    scripts: [
      breadcrumbScript([
        { name: "Home", path: "/" },
        { name: "Test Tracker", path: "/tests" },
      ]),
    ],
  }),
  component: TestsPage,
});

function TestsPage() {
  const today = localDateString();
  const queryClient = useQueryClient();
  const { requirePremium } = usePremium();
  const showQuotaUpgrade = useQuotaUpgrade();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [warning, setWarning] = useState<string | null>(null);

  const { data, isPending } = useQuery({ queryKey: ["tests"], queryFn: () => fetchTests() });
  const tests = useMemo<TestPlan[]>(() => data ?? [], [data]);

  const create = useMutation({
    mutationFn: (input: { name: string; testDate: string; chapters: TestChapterInput[] }) =>
      createTest({ data: { ...input, today } }),
    retry: 2,
    onMutate: () => {
      const stop = trackSave("create-test");
      setSheetOpen(false);
      setThinking(true);
      notifySaved("Saved Successfully");
      return { stop };
    },
    onSuccess: (result, _input, ctx) => {
      ctx?.stop("ok");
      queryClient.invalidateQueries({ queryKey: ["tests"] });
      window.setTimeout(() => {
        setThinking(false);
        setWarning(result.warning);
      }, 900);
    },
    onError: (err: Error, _input, ctx) => {
      ctx?.stop("error");
      setThinking(false);
      showQuotaUpgrade(err);
      setWarning(friendlyError(err));
    },
  });

  return (
    <main className="mx-auto min-h-screen w-full max-w-7xl space-y-6 px-4 pt-8 sm:px-6 lg:px-8">
      <motion.header
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 240, damping: 26 }}
        className="flex items-start justify-between gap-3"
      >
        <div>
          <h1 className="text-[26px] font-bold tracking-tight">
            Test Tracker <AccessBadge level="PRO + FREE" />
          </h1>
          <p className="mt-1 text-[13px] text-muted-foreground">
            Every test gets its own schedule, score and analytics.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <ShareButton />
          <span className="neural-gradient neural-halo relative isolate z-0 flex size-11 shrink-0 items-center justify-center rounded-2xl">
            <Crosshair className="size-5 text-white" />
          </span>
        </div>
      </motion.header>


      <AnimatePresence mode="wait">
        {thinking ? (
          <ThinkingCard key="thinking" />
        ) : isPending ? (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="glass mt-6 h-40 animate-pulse rounded-[24px]"
          />
        ) : tests.length === 0 ? (
          <EmptyState key="empty" onAdd={() => setSheetOpen(true)} />
        ) : (
          <motion.div
            key="list"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ type: "spring", stiffness: 230, damping: 28 }}
            className="mt-6 space-y-4"
          >
            <NeuralButton
              className="w-full"
              onClick={() => (requirePremium() ? setSheetOpen(true) : undefined)}
            >
              <Plus className="size-4" /> Add Test
            </NeuralButton>

            <Link
              to="/tests/performance"
              className="glass flex items-center gap-3 rounded-[22px] px-5 py-4"
            >
              <BarChart3 className="size-5 shrink-0 text-primary" />
              <span className="min-w-0 flex-1">
                <span className="block text-[14px] font-bold tracking-tight">Performance</span>
                <span className="block text-[12px] text-muted-foreground">
                  Scores, subject trends, AI insights &amp; history
                </span>
              </span>
              <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
            </Link>

            <QuotaNotice message={warning} tone="warning" />

             <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {tests.map((test, index) => (
                <TestSummaryCard key={test.id} test={test} today={today} index={index} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AddTestSheet
        open={sheetOpen}
        busy={create.isPending}
        onClose={() => setSheetOpen(false)}
        onSubmit={(input) => (requirePremium() ? create.mutate(input) : undefined)}
      />
    </main>
  );
}

function TestSummaryCard({
  test,
  today,
  index,
}: {
  test: TestPlan;
  today: string;
  index: number;
}) {
  const progress = planProgress(test);
  const status = testStatus(test, today);
  const daysLeft = Math.max(0, daysBetween(today, test.testDate));

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.05, 0.25), type: "spring", stiffness: 240, damping: 26 }}
    >
      <Link
        to="/tests/$testId"
        params={{ testId: test.id }}
        className="glass block rounded-[26px] p-5 transition-transform active:scale-[0.985]"
      >
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-[17px] font-bold tracking-tight">{test.name}</h2>
            <p className="mt-1 text-[12.5px] text-muted-foreground">
              {formatPrettyDate(test.testDate)}
            </p>
          </div>
          <span
            className={cn(
              "shrink-0 rounded-full border px-3 py-1 text-[11px] font-bold",
              STATUS_STYLE[status],
            )}
          >
            {STATUS_LABEL[status]}
          </span>
        </div>

        <div className="mt-4 flex items-end justify-between gap-3">
          <div>
            <p className="text-[11.5px] font-semibold text-muted-foreground">
              {progress.done}/{progress.total} tasks done
            </p>
            {test.score ? (
              <p className="mt-1 text-[13px] font-bold">
                Score{" "}
                <span className="neural-text">
                  {test.score.obtained}/{test.score.total}
                </span>{" "}
                <span className="text-muted-foreground">
                  · {percentOf(test.score.obtained, test.score.total)}%
                </span>
              </p>
            ) : null}
          </div>
          {status === "upcoming" || status === "today" ? (
            <p className="shrink-0 text-right">
              <span className="text-[20px] font-extrabold leading-none neural-text">
                {daysLeft}
              </span>
              <span className="ml-1 text-[11px] font-semibold text-muted-foreground">
                {daysLeft === 1 ? "day left" : "days left"}
              </span>
            </p>
          ) : null}
        </div>

        <div className="mt-3 flex items-center gap-3">
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-surface">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progress.percent}%` }}
              transition={{ type: "spring", stiffness: 120, damping: 24 }}
              className="neural-gradient h-full rounded-full"
            />
          </div>
          <span className="shrink-0 text-[11.5px] font-bold text-muted-foreground">
            {progress.percent}%
          </span>
          <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
        </div>
      </Link>
    </motion.div>
  );
}

function ThinkingCard() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ type: "spring", stiffness: 230, damping: 28 }}
      className="glass relative mt-6 overflow-hidden rounded-[24px] p-6"
    >
      <div
        aria-hidden
        className="thinking-sweep neural-gradient pointer-events-none absolute inset-y-0 -left-1/3 w-2/3 opacity-40 blur-2xl"
      />
      <div className="relative flex items-center gap-3">
        <Sparkles className="size-5 text-primary" />
        <div>
          <p className="text-[15px] font-bold tracking-tight">Building your schedule…</p>
          <p className="mt-1 text-[12.5px] text-muted-foreground">
            Balancing weak chapters, revisions and question practice.
          </p>
        </div>
      </div>
      <div className="relative mt-5 space-y-2.5">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-10 animate-pulse rounded-2xl bg-surface" />
        ))}
      </div>
    </motion.div>
  );
}

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ type: "spring", stiffness: 230, damping: 28 }}
      className="glass mt-8 flex flex-col items-center rounded-[26px] px-6 py-12 text-center"
    >
      <span className="neural-gradient neural-halo relative isolate z-0 flex size-16 items-center justify-center rounded-3xl">
        <Crosshair className="size-7 text-white" />
      </span>
      <h2 className="mt-5 text-[18px] font-bold tracking-tight">No test added yet</h2>
      <p className="mt-1.5 max-w-[16rem] text-[13px] leading-relaxed text-muted-foreground">
        Add your next test and we&apos;ll build a day-by-day revision and practice plan for it.
      </p>
      <NeuralButton className="mt-6" onClick={onAdd}>
        <Plus className="size-4" /> Add Test
      </NeuralButton>
    </motion.div>
  );
}
